// 변경공사 오류 — 화면은 문구가 아니라 타입으로 가른다(ConfirmRequiredError 선례).

import { ConfirmRequiredError } from "../../lib/confirm-error";
import { formatQty, formatWon } from "../../lib/format";
import {
  CHANGE_ORDER_BASE_STALE_MESSAGE,
  CHANGE_ORDER_DEDUCT_OVER_HINT,
  CHANGE_ORDER_SENT_CONFIRM_HINT,
  CHANGE_ORDER_SOURCE_SCOPE_MESSAGE,
  CHANGE_ORDER_STALE_HINT,
  CHANGE_ORDER_WARRANTY_EXTEND_HINT,
  CHANGE_ORDER_WARRANTY_EXTEND_MESSAGE,
  paidWithSentConfirmMessage,
  statementDetailMissingMessage,
} from "./messages";

// 유지보수 현장 변경공사에 공기 증감이 실렸다 — 비워야 풀린다.
export class WarrantyExtendError extends Error {
  constructor() {
    super(CHANGE_ORDER_WARRANTY_EXTEND_MESSAGE);
    this.name = "WarrantyExtendError";
  }
}

export const isWarrantyExtend = (error: unknown): error is WarrantyExtendError => error instanceof WarrantyExtendError;

// `0067` 이 hint 로 표지한 유지보수 공기 증감 거절이면 타입 오류로 바꾼다(아니면 null).
export const warrantyExtendFromRpc = (error: { hint?: string | null }): WarrantyExtendError | null =>
  error.hint === CHANGE_ORDER_WARRANTY_EXTEND_HINT ? new WarrantyExtendError() : null;

// 기지급 합보다 낮게 감액하려 했다 — 이미 받은 돈을 돌려줄 회차가 없다.
export class BelowPaidError extends Error {
  readonly paid: number;
  readonly next_total: number;

  constructor(paid: number, nextTotal: number) {
    super(
      `이미 받은 금액(${formatWon(paid)})보다 낮게 줄일 수 없습니다 — 변경 후 ${formatWon(nextTotal)}`
    );
    this.name = "BelowPaidError";
    this.paid = paid;
    this.next_total = nextTotal;
  }
}

// ★3·★4 대조 실패 — 다른 변경공사·지급 기록이 먼저 반영됐다. 새로고침 후 다시 확인한다.
export class ChangeOrderStaleError extends Error {
  constructor(message: string = CHANGE_ORDER_BASE_STALE_MESSAGE) {
    super(message);
    this.name = "ChangeOrderStaleError";
  }
}

export const isBelowPaid = (error: unknown): error is BelowPaidError =>
  error instanceof BelowPaidError;

export const isChangeOrderStale = (error: unknown): error is ChangeOrderStaleError =>
  error instanceof ChangeOrderStaleError;

// PostgREST 오류의 hint 가 ★ 대조 표지면 타입 오류로 바꾼다(아니면 null).
export const staleFromRpc = (error: {
  message?: string;
  hint?: string | null;
}): ChangeOrderStaleError | null =>
  error.hint === CHANGE_ORDER_STALE_HINT ? new ChangeOrderStaleError(error.message) : null;

// 지급 기록을 막은 hint 면 차수로 문구를 지은 확인 오류로 바꾼다(아니면 null).
export const sentConfirmFromRpc = (error: {
  details?: string | null;
  hint?: string | null;
}): ConfirmRequiredError | null => {
  if (error.hint !== CHANGE_ORDER_SENT_CONFIRM_HINT) return null;
  const seqs = (error.details ?? "")
    .split(",")
    .map((v) => Number(v.trim()))
    .filter((n) => Number.isInteger(n) && n > 0);
  return new ConfirmRequiredError(paidWithSentConfirmMessage(seqs));
};

// 연결한 원 견적 줄이 이 계약 견적(같은 조직)의 맨 끝 항목이 아니다.
export class QuoteSourceScopeError extends Error {
  constructor() {
    super(CHANGE_ORDER_SOURCE_SCOPE_MESSAGE);
    this.name = "QuoteSourceScopeError";
  }
}

export const isQuoteSourceScope = (error: unknown): error is QuoteSourceScopeError =>
  error instanceof QuoteSourceScopeError;

// 감액이 원 견적 줄에서 앞서 승인된 변경공사를 반영하고 남은 수량·금액을 넘는다.
export class DeductOverRemainingError extends Error {
  readonly source_quote_item_id: string;
  readonly remaining_qty: number;
  readonly remaining_amount: number;

  constructor(sourceId: string, label: string, remainingQty: number, remainingAmount: number, unit: string) {
    super(
      `「${label}」 감액이 원 견적에 남은 양을 넘습니다 — 남은 수량 ${formatQty(remainingQty)}${unit} · 남은 금액 ${formatWon(remainingAmount)}`
    );
    this.name = "DeductOverRemainingError";
    this.source_quote_item_id = sourceId;
    this.remaining_qty = remainingQty;
    this.remaining_amount = remainingAmount;
  }
}

export const isDeductOverRemaining = (error: unknown): error is DeductOverRemainingError =>
  error instanceof DeductOverRemainingError;

// `0073` 이 hint 로 표지한 승인 때 잔량 초과면 detail JSON 으로 같은 타입 오류를 짓는다(아니면 null · detail 이 깨졌으면 id 없이 문구만).
export const deductOverFromRpc = (error: {
  message?: string;
  details?: string | null;
  hint?: string | null;
}): DeductOverRemainingError | null => {
  if (error.hint !== CHANGE_ORDER_DEDUCT_OVER_HINT) return null;
  let d: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(error.details ?? "");
    if (parsed && typeof parsed === "object") d = parsed as Record<string, unknown>;
  } catch {
    // 표지만 믿고 아래 기본값으로 짓는다.
  }
  const text = (v: unknown, fallback: string) => (typeof v === "string" && v ? v : fallback);
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? Math.max(v, 0) : 0);
  return new DeductOverRemainingError(text(d.id, ""), text(d.label, "원 견적 줄"), num(d.qty), num(d.amount), text(d.unit, ""));
};

// 현재 내역서 — 승인된 변경공사 목록과 항목 묶음이 어긋났다(합계가 틀린 채 보이지 않게 멈춘다).
export class StatementDetailMissingError extends Error {
  readonly seq: number;

  constructor(seq: number) {
    super(statementDetailMissingMessage(seq));
    this.name = "StatementDetailMissingError";
    this.seq = seq;
  }
}

export const isStatementDetailMissing = (error: unknown): error is StatementDetailMissingError =>
  error instanceof StatementDetailMissingError;
